<template>
  <auth-shell
    :title="$t('auth.createStore.title')"
    :subtitle="$t('auth.createStore.sub')"
    :back-label="$t('auth.skip')"
    icon="fal fa-store"
    icon-tone="lime"
    wide
  >
    <form class="ps-auth-form" novalidate @submit.prevent="submitForm(form)">
      <!-- store name -->
      <div class="ps-field">
        <label class="ps-field__label" for="osName">{{
          $t("auth.createStore.storeName")
        }}</label>
        <input
          id="osName"
          v-model="form.name"
          type="text"
          required
          :placeholder="$t('auth.createStore.storeNamePh')"
          class="ps-input"
          :disabled="overlay"
          @input="$v.form.name.$touch()"
          @blur="$v.form.name.$touch()"
        />
        <p v-if="nameError.length" class="ps-field__error">
          {{ nameError[0] }}
        </p>
      </div>

      <!-- store logo -->
      <div class="ps-field">
        <span class="ps-field__label">{{ $t("store_logo") }}</span>
        <DragDropUpload
          v-model="imgFile"
          accept="image/*"
          :max-size-mb="5"
          @file-selected="onFileSelected"
          @file-removed="onFileRemoved"
        />
      </div>

      <!-- store type -->
      <div class="ps-field">
        <span class="ps-field__label">{{ $t("choose_type") }}</span>
        <v-radio-group
          v-model="form.store_type_code"
          class="py-0 my-0 ps-radio-row"
          row
        >
          <v-radio
            v-for="(item, index) in store_type"
            :key="index"
            :label="item.text"
            :value="item.value"
            color="primary"
          ></v-radio>
        </v-radio-group>
      </div>

      <!-- cost calculation method -->
      <div class="ps-field">
        <span class="ps-field__label">{{
          $t("cost_calculation_method")
        }}</span>
        <v-radio-group
          v-model="form.cost_calculation_method"
          class="py-0 my-0"
          column
          color="primary"
        >
          <v-radio
            v-for="(item, index) in cost_methods"
            :key="index"
            :value="item.value"
            color="primary"
          >
            <template v-slot:label>
              <div>
                <div class="font-weight-medium">
                  {{ $t(item.label) }}
                </div>
                <div class="text-caption grey--text">
                  {{ $t(item.description) }}
                </div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
        <v-alert dense type="info" class="mt-2 mb-1">
          {{ $t("cost_method_cannot_change") }}
        </v-alert>
      </div>

      <!-- phone -->
      <div class="ps-field">
        <label class="ps-field__label" for="osPhone">{{
          $t("auth.createStore.phone")
        }}</label>
        <input
          id="osPhone"
          v-model="form.mobile_no"
          type="tel"
          required
          :maxlength="10"
          :placeholder="$t('auth.createStore.phonePh')"
          class="ps-input"
          :disabled="overlay"
          @input="$v.form.mobile_no.$touch()"
          @blur="$v.form.mobile_no.$touch()"
        />
        <p v-if="mobile_noError.length" class="ps-field__error">
          {{ mobile_noError[0] }}
        </p>
      </div>

      <!-- address -->
      <div class="ps-field">
        <label class="ps-field__label" for="osAddress">{{
          $t("auth.createStore.address")
        }}</label>
        <input
          id="osAddress"
          v-model="form.address"
          type="text"
          :placeholder="$t('auth.createStore.addressPh')"
          class="ps-input"
          :disabled="overlay"
        />
      </div>

      <button
        type="submit"
        class="ps-btn ps-btn--brand ps-btn--block ps-auth-submit"
        :disabled="overlay"
      >
        {{ overlay ? "…" : $t("auth.createStore.submit") }}
      </button>
    </form>

    <app-loading :overlay="overlay" />
  </auth-shell>
</template>

<script>
import { toast } from "@/controllers/alert";

import {
  required,
  maxLength,
  minLength,
  numeric,
} from "vuelidate/lib/validators";
import DragDropUpload from "@/components/global/DragDropUpload.vue";
import Cookies from "js-cookie";
import { authenticated } from "@/controllers/auth";
import AuthShell from "@/components/auth/AuthShell.vue";
export default {
  name: "OpenStorePage",
  layout: "blank",
  components: { DragDropUpload, AuthShell },
  data: () => ({
    overlay: false,

    form: {
      name: "",
      mobile_no: "20",
      address: "",
      store_type_code: "POS",
      cost_calculation_method: "WAC",
    },
    imgFile: null,
    cost_methods: [
      {
        value: "FIFO",
        label: "cost_method_fifo",
        description: "cost_method_description_fifo",
      },
      {
        value: "LIFO",
        label: "cost_method_lifo",
        description: "cost_method_description_lifo",
      },
      {
        value: "WAC",
        label: "cost_method_wac",
        description: "cost_method_description_wac",
      },
    ],
  }),
  validations: {
    form: {
      name: { required },
      mobile_no: {
        required,
        numeric,
        maxLength: maxLength(10),
        minLength: minLength(10),
      },
    },
  },

  computed: {
    store_type() {
      return [
        { text: this.$t("store_type_retail"), value: "POS" },
        { text: this.$t("store_type_restaurant"), value: "RESTAURANT" },
        { text: this.$t("store_type_buffet"), value: "BUFFET" },
      ];
    },
    nameError() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      !this.$v.form.name.required &&
        errors.push(this.$t("validation_name_required"));
      return errors;
    },
    mobile_noError() {
      const errors = [];
      if (!this.$v.form.mobile_no.$dirty) return errors;
      !this.$v.form.mobile_no.required &&
        errors.push(this.$t("validation_mobile_required"));
      if (!this.$v.form.mobile_no.numeric) {
        errors.push(this.$t("validation_mobile_numeric"));
      }
      if (!this.$v.form.mobile_no.minLength) {
        errors.push(this.$t("validation_mobile_length"));
      }
      if (!this.$v.form.mobile_no.maxLength) {
        errors.push(this.$t("validation_mobile_length"));
      }

      return errors;
    },
  },
  methods: {
    async submitForm(form) {
      try {
        this.$v.form.$touch();
        if (!this.$v.form.$invalid) {
          const token = Cookies.get("token");
          this.overlay = true;
          // Form data
          const formData = new FormData();
          formData.append("name", form.name);
          formData.append("mobile_no", form.mobile_no);
          formData.append("address", form.address);
          formData.append("token", token);
          formData.append("store_type_code", form.store_type_code);
          formData.append(
            "cost_calculation_method",
            form.cost_calculation_method
          );
          if (this.$route.query?.source) {
            formData.append("source", this.$route.query.source);
          }
          if (String(this.$route.query?.source || "") === "marketplace") {
            formData.append("enable_online", "1");
          }
          if (this.imgFile) {
            formData.append("file", this.imgFile);
          }
          const { data } = await this.$http.post("open_store", formData);
          if (data.status) {
            await toast(this, "store_open_success");
            await authenticated(this, data);
          }
        }
      } catch (error) {
        this.overlay = false;
        console.log("submitForm ===> ", error);
      }
    },
    onFileSelected() {
      // no-op: v-model already bound
    },
    onFileRemoved() {
      // no-op: v-model already cleared
    },
  },
};
</script>

<style scoped>
.ps-auth-form {
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ps-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ps-field__label {
  font-size: 0.875rem;
  font-weight: 900;
  color: var(--ps-ink);
}

.ps-field__error {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
}

.ps-auth-submit {
  padding: 1rem;
}

.ps-radio-row {
  gap: 0;
}
</style>
